import type { ColumnDef, PaginationState, SortingState } from "@tanstack/react-table";
import { Plus } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { DataTable, type DataTableFacet } from "@/components/data-table";
import type { ExportRow } from "@/components/data-table/export-utils";
import type { DataTableFeatures } from "@/components/data-table/features";
import { RowActionsMenu } from "@/components/data-table/row-actions-menu";
import { Badge } from "@/components/ui/badge";
import { authClient, useSession } from "@/lib/auth/client";
import { BanUserDialog } from "./BanUserDialog";
import { CreateUserDialog } from "./CreateUserDialog";
import { DeleteUserDialog } from "./DeleteUserDialog";
import { EditUserDialog } from "./EditUserDialog";
import { ResetPasswordDialog } from "./ResetPasswordDialog";

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  banned: boolean;
  banReason?: string | null;
  banExpires?: string | null;
  createdAt: Date;
}

type DialogState =
  | { type: "none" }
  | { type: "create" }
  | { type: "edit"; user: User }
  | { type: "resetPassword"; user: User }
  | { type: "ban"; user: User }
  | { type: "delete"; user: User };

const USER_FACETS: DataTableFacet[] = [
  {
    column: "role",
    title: "Role",
    options: [
      { label: "Admin", value: "admin" },
      { label: "User", value: "user" },
    ],
  },
  {
    column: "status",
    title: "Status",
    options: [
      { label: "Active", value: "active" },
      { label: "Banned", value: "banned" },
    ],
  },
];

function toExportRows(users: User[]): ExportRow[] {
  return users.map((u) => ({
    Name: u.name,
    Email: u.email,
    Role: u.role,
    Status: u.banned ? "Banned" : "Active",
    Created: new Date(u.createdAt).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
  }));
}

export function UsersTable() {
  const { data: session } = useSession();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>([{ id: "createdAt", desc: true }]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const [activeFacets, setActiveFacets] = useState<Record<string, string[]>>({});
  const [dialog, setDialog] = useState<DialogState>({ type: "none" });
  const [providers, setProviders] = useState<Record<string, string[]>>({});

  /* ---- Action ref pattern: stable reference for column cells ---- */
  const actionsRef = useRef({
    openEdit: (_user: User) => {},
    openResetPassword: (_user: User) => {},
    revokeSessions: (_user: User) => {},
    openBan: (_user: User) => {},
    unban: (_user: User) => {},
    openDelete: (_user: User) => {},
    sessionUserId: undefined as string | undefined,
  });

  actionsRef.current = {
    openEdit: (user: User) => setDialog({ type: "edit", user }),
    openResetPassword: (user: User) => setDialog({ type: "resetPassword", user }),
    revokeSessions: async (user: User) => {
      const { error: err } = await authClient.admin.revokeUserSessions({ userId: user.id });
      if (err) {
        toast.error(err.message ?? "Failed to revoke sessions");
        return;
      }
      toast.success(`All sessions revoked for ${user.name}`);
    },
    openBan: (user: User) => setDialog({ type: "ban", user }),
    unban: async (user: User) => {
      const { error: err } = await authClient.admin.unbanUser({ userId: user.id });
      if (err) {
        toast.error(err.message ?? "Failed to unban user");
        return;
      }
      toast.success(`${user.name} has been unbanned`);
      fetchUsers();
    },
    openDelete: (user: User) => setDialog({ type: "delete", user }),
    sessionUserId: session?.user?.id,
  };

  /* ---- Data fetching ---- */
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { data, error: err } = await authClient.admin.listUsers({
        query: {
          limit: 100,
        },
      });
      if (err) {
        toast.error(err.message ?? "Failed to load users");
        setError(true);
        return;
      }
      setUsers((data?.users as User[]) ?? []);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProviders = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/user-providers");
      if (res.ok) setProviders(await res.json());
    } catch {}
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchProviders();
  }, [fetchUsers, fetchProviders]);

  /* ---- Columns (stable via useMemo + ref for actions) ---- */
  const columns = useMemo<ColumnDef<DataTableFeatures, User, unknown>[]>(
    () => [
      {
        accessorKey: "name",
        header: "User",
        cell: ({ row }) => {
          const user = row.original;
          return (
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                {user.name?.charAt(0).toUpperCase() ?? "U"}
              </div>
              <div className="min-w-0">
                <p className="font-medium truncate">{user.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              </div>
            </div>
          );
        },
      },
      {
        id: "role",
        accessorFn: (row) => row.role,
        header: "Role",
        cell: ({ row }) => {
          const role = row.original.role;
          return (
            <Badge
              variant="secondary"
              className={
                role === "admin" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
              }
            >
              {role}
            </Badge>
          );
        },
        enableSorting: true,
      },
      {
        id: "provider",
        header: "Provider",
        enableSorting: false,
        cell: ({ row }) => {
          const userProviders = providers[row.original.id] ?? [];
          return (
            <div className="flex flex-wrap gap-1">
              {userProviders.map((p) => {
                const label = p === "credential" ? "Local" : p;
                const style =
                  p === "credential"
                    ? "bg-slate-500/10 text-slate-600 dark:text-slate-400"
                    : "bg-blue-500/10 text-blue-600 dark:text-blue-400";
                return (
                  <Badge key={p} variant="secondary" className={style}>
                    {label}
                  </Badge>
                );
              })}
            </div>
          );
        },
      },
      {
        id: "status",
        accessorFn: (row) => (row.banned ? "banned" : "active"),
        header: "Status",
        cell: ({ row }) => {
          const user = row.original;
          if (user.banned) {
            return (
              <div>
                <Badge variant="secondary" className="bg-red-500/10 text-red-600 dark:text-red-400">
                  Banned
                </Badge>
                {user.banReason && (
                  <p className="mt-0.5 text-[11px] text-muted-foreground truncate max-w-[160px]">
                    {user.banReason}
                  </p>
                )}
              </div>
            );
          }
          return (
            <Badge
              variant="secondary"
              className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
            >
              Active
            </Badge>
          );
        },
        enableSorting: true,
      },
      {
        id: "createdAt",
        accessorFn: (row) => new Date(row.createdAt).getTime(),
        header: "Created",
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {new Date(row.original.createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        ),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => {
          const user = row.original;
          const isSelf = user.id === actionsRef.current.sessionUserId;
          if (isSelf) return null;

          const normalActions = [
            {
              label: "Edit User",
              onClick: () => actionsRef.current.openEdit(user),
            },
            {
              label: "Reset Password",
              onClick: () => actionsRef.current.openResetPassword(user),
            },
            {
              label: "Revoke Sessions",
              onClick: () => actionsRef.current.revokeSessions(user),
            },
          ];

          const destructiveActions = [
            ...(user.banned
              ? [
                  {
                    label: "Unban User",
                    onClick: () => actionsRef.current.unban(user),
                  },
                ]
              : [
                  {
                    label: "Ban User",
                    onClick: () => actionsRef.current.openBan(user),
                  },
                ]),
            {
              label: "Delete User",
              onClick: () => actionsRef.current.openDelete(user),
            },
          ];

          return (
            <RowActionsMenu
              label={`Actions for ${user.name}`}
              actions={normalActions}
              destructiveActions={destructiveActions}
            />
          );
        },
      },
    ],
    [providers],
  );

  /* ---- Client-side filtering ---- */
  const filtered = useMemo(() => {
    return users.filter((u) => {
      if (search) {
        const q = search.toLowerCase();
        const match = u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
        if (!match) return false;
      }
      const roleValues = activeFacets.role ?? [];
      if (roleValues.length > 0 && !roleValues.includes(u.role)) return false;
      const statusValues = activeFacets.status ?? [];
      if (statusValues.length > 0) {
        const userStatus = u.banned ? "banned" : "active";
        if (!statusValues.includes(userStatus)) return false;
      }
      return true;
    });
  }, [users, search, activeFacets]);

  const exportRows = useMemo(() => toExportRows(filtered), [filtered]);

  /* ---- Stats ---- */
  const totalUsers = users.length;
  const adminCount = users.filter((u) => u.role === "admin").length;
  const bannedCount = users.filter((u) => u.banned).length;

  /* ---- Callbacks ---- */
  const handleFacetChange = useCallback((column: string, values: string[]) => {
    setActiveFacets((prev) => ({ ...prev, [column]: values }));
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const rowIdAccessor = useCallback((row: User) => row.id, []);

  function closeDialog() {
    setDialog({ type: "none" });
  }

  /* ---- Header actions ---- */
  const headerActions = useMemo(
    () => [
      {
        label: "Create User",
        icon: Plus,
        onClick: () => setDialog({ type: "create" }),
      },
    ],
    [],
  );

  /* ---- Stats cards as headerExtra ---- */
  const statsCards = (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
      <div className="rounded-xl border border-border/60 bg-card p-4">
        <p className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground">
          Total Users
        </p>
        <p className="mt-1 text-2xl font-bold tracking-tight">{totalUsers}</p>
      </div>
      <div className="rounded-xl border border-border/60 bg-card p-4">
        <p className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground">
          Admins
        </p>
        <p className="mt-1 text-2xl font-bold tracking-tight">{adminCount}</p>
      </div>
      <div className="rounded-xl border border-border/60 bg-card p-4">
        <p className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground">
          Banned
        </p>
        <p className="mt-1 text-2xl font-bold tracking-tight text-destructive">{bannedCount}</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {statsCards}

      <DataTable
        title="Users"
        subtitle="Manage user accounts"
        badge={filtered.length}
        actions={headerActions}
        columns={columns}
        data={filtered}
        rowIdAccessor={rowIdAccessor}
        search={search}
        onSearchChange={handleSearchChange}
        searchPlaceholder="Search by name or email..."
        facets={USER_FACETS}
        activeFacets={activeFacets}
        onFacetChange={handleFacetChange}
        sorting={sorting}
        onSortingChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        pageCount={Math.ceil(filtered.length / pagination.pageSize)}
        filteredRowCount={filtered.length}
        enablePagination
        enableSorting
        exportFileName="users"
        exportRows={exportRows}
        isLoading={loading}
        isError={error}
      />

      {/* Dialogs */}
      <CreateUserDialog
        open={dialog.type === "create"}
        onOpenChange={(open) => !open && closeDialog()}
        onSuccess={fetchUsers}
      />

      {dialog.type === "edit" && (
        <EditUserDialog
          open
          onOpenChange={(open) => !open && closeDialog()}
          userId={dialog.user.id}
          userName={dialog.user.name}
          userRole={dialog.user.role}
          onSuccess={fetchUsers}
        />
      )}

      {dialog.type === "resetPassword" && (
        <ResetPasswordDialog
          open
          onOpenChange={(open) => !open && closeDialog()}
          userId={dialog.user.id}
          userName={dialog.user.name}
        />
      )}

      {dialog.type === "ban" && (
        <BanUserDialog
          open
          onOpenChange={(open) => !open && closeDialog()}
          userId={dialog.user.id}
          userName={dialog.user.name}
          onSuccess={fetchUsers}
        />
      )}

      {dialog.type === "delete" && (
        <DeleteUserDialog
          open
          onOpenChange={(open) => !open && closeDialog()}
          userId={dialog.user.id}
          userName={dialog.user.name}
          onSuccess={fetchUsers}
        />
      )}
    </div>
  );
}
